import type { AdminPortalUser } from '../constants/adminPortal';
import { getAdminPortalUser } from './adminPortalSession';
import { getPersistedSessionRole, getClientAccessToken } from './sessionAuth';

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  try {
    const parts = token.split('.');
    if (parts.length < 2) {
      return null;
    }

    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const paddedPayload = payload.padEnd(Math.ceil(payload.length / 4) * 4, '=');
    const decoded = window.atob(paddedPayload);
    return JSON.parse(decoded) as Record<string, unknown>;
  } catch {
    return null;
  }
};

export const getCurrentUserRole = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const token = getClientAccessToken();
  if (token) {
    const payload = decodeJwtPayload(token);
    const role = payload?.role;
    if (typeof role === 'string') {
      return role;
    }
  }

  const persistedRole = getPersistedSessionRole();
  if (persistedRole) {
    return persistedRole;
  }

  const portalUser = getAdminPortalUser();
  if (typeof portalUser?.role === 'string') {
    return portalUser.role;
  }

  const partnerRaw = localStorage.getItem('partnerUser');
  if (partnerRaw) {
    try {
      const partnerUser = JSON.parse(partnerRaw) as { role?: string };
      if (typeof partnerUser.role === 'string') {
        return partnerUser.role;
      }
    } catch {
      /* ignore */
    }
  }

  return null;
};

export const isAdminUserProfile = (): boolean => getCurrentUserRole() === 'admin';

export const isViewRoleProfile = (): boolean => getCurrentUserRole() === 'view';

export const isSupportRoleProfile = (): boolean => getCurrentUserRole() === 'support';

export const isExpertRoleProfile = (): boolean => getCurrentUserRole() === 'expert';

export const isAdminPortalUser = (): boolean => {
  const role = getCurrentUserRole();
  return role === 'admin' || role === 'view';
};

export const isSupportPortalUser = (): boolean => getCurrentUserRole() === 'support';

export const isExpertPortalUser = (): boolean => getCurrentUserRole() === 'expert';

export const isPortalOnlySession = (): boolean =>
  Boolean(getAdminPortalUser()) && !localStorage.getItem('msmeRegistration');

export const getPortalUserDisplayName = (user?: AdminPortalUser | null): string => {
  const portalUser = user ?? getAdminPortalUser();
  if (!portalUser) {
    return '';
  }

  const name = [portalUser.profile?.firstName, portalUser.profile?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();

  return name || portalUser.email || '';
};
